// ── Streak tracking via localStorage ─────────────────────────────────────────
// Tracks daily play streaks without requiring user accounts.
// A streak increments when a player completes any puzzle on a new calendar day.
// Missing a day resets the streak to 1 on the next completion.

const STREAK_KEY = 'htg_streak';

/**
 * Load streak data from localStorage.
 * Returns { current, best, lastPlayedDate }
 */
export function loadStreak() {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return { current: 0, best: 0, lastPlayedDate: null };
    return JSON.parse(raw);
  } catch {
    return { current: 0, best: 0, lastPlayedDate: null };
  }
}

/**
 * Record a completed puzzle and update the streak.
 * @param {string} dateStr - puzzle publish_date (YYYY-MM-DD)
 * @returns {{ current, best, lastPlayedDate, isNew: bool }}
 */
export function recordStreakPlay(dateStr) {
  const data = loadStreak();

  // Already recorded for this date — don't double-count
  if (data.lastPlayedDate === dateStr) {
    return { ...data, isNew: false };
  }

  // Check if this date is consecutive with the last played date
  const isConsecutive = (() => {
    if (!data.lastPlayedDate) return false;
    const [ly, lm, ld] = data.lastPlayedDate.split('-').map(Number);
    const [cy, cm, cd] = dateStr.split('-').map(Number);
    const last    = new Date(ly, lm - 1, ld);
    const current = new Date(cy, cm - 1, cd);
    const diffDays = Math.round((current - last) / 86400000);
    return diffDays === 1;
  })();

  const newCurrent = isConsecutive ? data.current + 1 : 1;
  const newBest    = Math.max(data.best, newCurrent);

  const updated = {
    current:        newCurrent,
    best:           newBest,
    lastPlayedDate: dateStr,
  };

  try {
    localStorage.setItem(STREAK_KEY, JSON.stringify(updated));
  } catch {}

  return { ...updated, isNew: true };
}

/**
 * Check if today's puzzle has already been played.
 */
export function hasPlayedToday(dateStr) {
  const { lastPlayedDate } = loadStreak();
  return lastPlayedDate === dateStr;
}
